/* eslint-disable react/no-unknown-property -- react-three/fiber uses JSX props ESLint doesn't recognise */
/**
 * SearchEllipsoidPanel — Sprint B · Feb 2026 (Leapfrog / Surpac-style).
 *
 * Presents the six ellipsoid controls (three ranges + azimuth / dip / plunge)
 * alongside a live 3-D preview canvas. The preview is fetched from
 * `POST /api/projects/{id}/estimation/ellipsoid-preview` which returns a
 * triangulated wireframe of the current ellipsoid — the same geometry
 * that the kriging / IDW pass uses server-side.
 *
 * When mounted with `onChange`, every parameter tweak bubbles up the
 * current settings so the parent (kriging / IDW dialog) can send them
 * along with its own compute request.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Loader2, Compass, Ruler, Layers } from "lucide-react";

const DEFAULT = {
  range_major: 60,
  range_semi_major: 40,
  range_minor: 20,
  azimuth: 0,
  dip: 0,
  plunge: 0,
};

function EllipsoidWire({ vertices, triangles }) {
  const geometry = useMemo(() => {
    if (!vertices?.length || !triangles?.length) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(vertices.flat()), 3));
    g.setIndex(new THREE.BufferAttribute(new Uint32Array(triangles.flat()), 1));
    g.computeVertexNormals();
    return g;
  }, [vertices, triangles]);
  if (!geometry) return null;
  return (
    <group>
      {/* solid, translucent hull */}
      <mesh geometry={geometry}>
        <meshStandardMaterial
          color="#4CAF50"
          transparent opacity={0.14}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>
      {/* crisp wireframe on top */}
      <lineSegments>
        <wireframeGeometry attach="geometry" args={[geometry]} />
        <lineBasicMaterial color="#2f6b1a" linewidth={1} />
      </lineSegments>
    </group>
  );
}

function AxisIndicator({ major, semi, minor, azimuth, dip, plunge }) {
  const R = useMemo(() => {
    const az = THREE.MathUtils.degToRad(azimuth);
    const dp = THREE.MathUtils.degToRad(dip);
    const pl = THREE.MathUtils.degToRad(plunge);
    // Match backend `ellipsoid_rotation_matrix`
    const majV = new THREE.Vector3(
      Math.cos(-dp) * Math.sin(az),
      Math.cos(-dp) * Math.cos(az),
      Math.sin(-dp),
    ).normalize();
    const horiz = new THREE.Vector3(Math.cos(az), -Math.sin(az), 0).normalize();
    const min0 = new THREE.Vector3().crossVectors(majV, horiz).normalize();
    const semiV = horiz.clone().multiplyScalar(Math.cos(pl))
      .add(min0.clone().multiplyScalar(Math.sin(pl))).normalize();
    const minV = new THREE.Vector3().crossVectors(majV, semiV).normalize();
    return { majV, semiV, minV };
  }, [azimuth, dip, plunge]);

  const arrow = (dir, len, color, tag) => {
    const end = dir.clone().multiplyScalar(len);
    const pts = [new THREE.Vector3(0, 0, 0), end];
    const g = new THREE.BufferGeometry().setFromPoints(pts);
    return (
      <group key={tag}>
        <line>
          <bufferGeometry attach="geometry" {...{ setFromPoints: undefined }} />
          <primitive object={g} attach="geometry" />
          <lineBasicMaterial attach="material" color={color} linewidth={2} />
        </line>
        <mesh position={end.toArray()}>
          <sphereGeometry args={[len * 0.03, 12, 12]} />
          <meshBasicMaterial color={color} />
        </mesh>
      </group>
    );
  };
  return (
    <group>
      {arrow(R.majV,  major, "#e11d48", "M")}
      {arrow(R.semiV, semi,  "#2563eb", "S")}
      {arrow(R.minV,  minor, "#7c3aed", "L")}
    </group>
  );
}

export default function SearchEllipsoidPanel({
  projectId, value, onChange, domains = [], onDomainChange,
  selectedDomain = null, className = "",
}) {
  const [params, setParams] = useState(() => ({ ...DEFAULT, ...(value || {}) }));
  const [mesh, setMesh]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const _timer = useRef(null);

  // Debounced fetch — regenerate wireframe 400 ms after last edit.
  useEffect(() => {
    if (!projectId) return;
    if (_timer.current) clearTimeout(_timer.current);
    _timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.post(
          `/projects/${projectId}/estimation/ellipsoid-preview`,
          { center: [0, 0, 0], ...params, n_stacks: 12, n_slices: 24 },
        );
        setMesh(data.mesh);
      } catch (e) {
        // fall back to a purely-client wireframe so the panel never blanks
        setMesh(null);
      } finally { setBusy(false); }
    }, 400);
    return () => _timer.current && clearTimeout(_timer.current);
  }, [projectId, params]);

  const update = (k, v) => {
    const num = Number(v);
    if (Number.isNaN(num)) return;
    const next = { ...params, [k]: num };
    setParams(next);
    onChange?.(next);
  };

  // Slider row helper — keeps the JSX below tidy.
  const controlRow = (key, label, min, max, step, unit, icon) => (
    <div className="space-y-1.5" data-testid={`ellipsoid-${key}-block`}>
      <div className="flex items-center justify-between text-[11px] font-mono">
        <span className="flex items-center gap-1.5 text-neutral-700">
          {icon}
          <span>{label}</span>
        </span>
        <span className="text-neutral-900 tabular-nums">
          {Number(params[key]).toFixed(step < 1 ? 1 : 0)} {unit}
        </span>
      </div>
      <Slider
        value={[params[key]]} min={min} max={max} step={step}
        onValueChange={(v) => update(key, v[0])}
        data-testid={`ellipsoid-${key}-slider`}
      />
      <Input
        type="number" step={step} min={min} max={max}
        value={params[key]}
        onChange={(e) => update(key, e.target.value)}
        data-testid={`ellipsoid-${key}-input`}
        className="h-8 rounded-none bg-input font-mono text-xs"
      />
    </div>
  );

  const volume_m3 = (4 / 3) * Math.PI *
    params.range_major * params.range_semi_major * params.range_minor;
  const anisotropy = params.range_minor > 0
    ? (params.range_major / params.range_minor).toFixed(2)
    : "—";

  return (
    <div className={`border border-border bg-card ${className}`} data-testid="search-ellipsoid-panel">
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Compass className="h-4 w-4 text-[#4CAF50]" strokeWidth={1.75} />
          <span className="font-heading font-bold text-sm">Search Ellipsoid</span>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />}
        </div>
        <span className="text-[10px] font-mono text-neutral-500">
          anisotropy&nbsp;<span className="text-neutral-900">{anisotropy}</span>&nbsp;·&nbsp;
          V&nbsp;<span className="text-neutral-900">{(volume_m3 / 1e6).toFixed(3)} Mm³</span>
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-4 p-4">
        {/* LEFT — controls */}
        <div className="space-y-4">
          <div className="grid gap-3">
            {controlRow("range_major",      "Range · Major",  0, 500, 1, "m", <Ruler className="h-3 w-3 text-[#e11d48]" />)}
            {controlRow("range_semi_major", "Range · Semi",   0, 500, 1, "m", <Ruler className="h-3 w-3 text-[#2563eb]" />)}
            {controlRow("range_minor",      "Range · Minor",  0, 500, 1, "m", <Ruler className="h-3 w-3 text-[#7c3aed]" />)}
          </div>
          <div className="grid gap-3 pt-2 border-t border-border">
            {controlRow("azimuth", "Azimuth (from N)", 0,   360, 1, "°", <Compass className="h-3 w-3" />)}
            {controlRow("dip",     "Dip (down = +)", -90,   90,  1, "°", <Compass className="h-3 w-3" />)}
            {controlRow("plunge",  "Plunge",         -90,   90,  1, "°", <Compass className="h-3 w-3" />)}
          </div>
          {(domains?.length ?? 0) > 0 && (
            <div className="pt-3 border-t border-border space-y-1.5"
                 data-testid="ellipsoid-domain-block">
              <Label className="text-[11px] font-mono flex items-center gap-1.5 text-neutral-700">
                <Layers className="h-3 w-3 text-[#4CAF50]" />
                Domain filter <span className="text-neutral-400">(lithology · hard boundary)</span>
              </Label>
              <select
                value={selectedDomain || ""}
                onChange={(e) => onDomainChange?.(e.target.value || null)}
                data-testid="ellipsoid-domain-select"
                className="w-full h-8 rounded-none border border-neutral-300 bg-white text-xs font-mono px-2"
              >
                <option value="">— No domain filter (all samples) —</option>
                {domains.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <p className="text-[10px] text-neutral-500">
                When set, only samples with matching lithology contribute to the estimate.
                This is a Leapfrog-style hard boundary — samples outside the domain are
                ignored even if they fall inside the ellipsoid.
              </p>
            </div>
          )}
        </div>

        {/* RIGHT — 3-D preview */}
        <div className="border border-neutral-200 bg-neutral-50 relative min-h-[280px]"
             data-testid="ellipsoid-canvas-wrap">
          <Canvas
            camera={{ position: [180, 180, 180], fov: 42 }}
            style={{ background: "#F8FAFC" }}
            data-testid="ellipsoid-3d-canvas"
          >
            <ambientLight intensity={0.65} />
            <directionalLight position={[100, 200, 100]} intensity={0.85} />
            <axesHelper args={[Math.max(params.range_major, 60) * 1.2]} />
            <gridHelper args={[400, 20, "#cbd5e1", "#e2e8f0"]} rotation={[Math.PI / 2, 0, 0]} />
            {mesh && (
              <EllipsoidWire vertices={mesh.vertices} triangles={mesh.triangles} />
            )}
            <AxisIndicator
              major={params.range_major}
              semi={params.range_semi_major}
              minor={params.range_minor}
              azimuth={params.azimuth}
              dip={params.dip}
              plunge={params.plunge}
            />
            <OrbitControls makeDefault />
          </Canvas>
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between
                          text-[10px] font-mono text-neutral-600 pointer-events-none">
            <span>
              <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "#e11d48" }} />Major&nbsp;
              <span className="inline-block w-2 h-2 rounded-full mx-1" style={{ background: "#2563eb" }} />Semi&nbsp;
              <span className="inline-block w-2 h-2 rounded-full mx-1" style={{ background: "#7c3aed" }} />Minor
            </span>
            <span>{mesh?.triangles?.length?.toLocaleString?.() ?? 0} tri</span>
          </div>
        </div>
      </div>
    </div>
  );
}
